/* Shared admin UI primitives + ImageUploader — DARK theme to match frontend brand. */
import { useRef, useState } from "react";
import api, { imgUrl } from "@/lib/api";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";

export function ImageUploader({ value, onChange, testid, label = "Image" }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">{label}</label>
      <div className="flex gap-2 items-start">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste an image URL or upload"
          data-testid={`${testid}-url-input`}
          className="admin-input flex-1"
        />
        <button type="button" onClick={() => fileRef.current?.click()} data-testid={`${testid}-upload-btn`} className="btn-outline shrink-0">
          <Upload className="h-4 w-4" /> {busy ? "..." : "Upload"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </div>
      {value && (
        <div className="mt-3 relative inline-block">
          <img src={imgUrl(value)} alt="" className="h-28 w-44 object-cover rounded-md border border-[var(--pz-line)]" />
          <button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 h-7 w-7 bg-[var(--pz-ink-2)] rounded-full shadow grid place-items-center text-rose-400 hover:bg-rose-500/20"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">{label}{required && <span className="text-[var(--pz-red)]"> *</span>}</label>
      <input
        type={type}
        required={required}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
        data-testid={testid}
        className="admin-input"
      />
    </div>
  );
}

export function TextArea({ label, value, onChange, rows = 4, testid, required }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-2">{label}{required && <span className="text-[var(--pz-red)]"> *</span>}</label>
      <textarea required={required} value={value ?? ""} rows={rows} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="admin-input" />
    </div>
  );
}

export function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <span className={`h-6 w-11 rounded-full transition relative ${checked ? "bg-[var(--pz-red)]" : "bg-[var(--pz-line)]"}`}>
        <span className={`absolute top-0.5 ${checked ? "left-5" : "left-0.5"} h-5 w-5 bg-white rounded-full shadow transition-all`} />
      </span>
      <span className="text-xs font-bold uppercase tracking-[0.15em] text-white">{label}</span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="sr-only" />
    </label>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">{title}</h1>
        {subtitle && <p className="text-zinc-400 mt-1 text-sm">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = "" }) {
  return <div className={`admin-panel rounded-md p-6 ${className}`}>{children}</div>;
}
